import { useState, useCallback, useLayoutEffect } from 'react';

type ElementDimensionType = {
  width: number;
  height: number;
  innerHeight: number;
  innerWidth: number;
};

/**
 * Observes an element and returns up-to-date width and height values.
 *
 * @param {node} [element=window] Element to observe and pull measurements from.
 *
 * @returns {object} Returns an object of:
 *  `width` - the given element's width
 *  `height` - the given element's height
 *
 * @example
 *
 * let [myElement, setMyElement] = React.useState(null)
 *
 * let { width, height } = useElementDimensions()
 *
 * return (
 *   <div
 *     ref={element => {
 *       setElement(element)
 *     }}
 *   >
 *     I am {height}px tall.
 *   </div>
 * )
 */

let getSize = (element: HTMLElement): ElementDimensionType => ({
  width: element?.offsetWidth,
  height: element?.offsetHeight,
  innerHeight: element?.scrollHeight,
  innerWidth: element?.scrollWidth,
});

let useElementDimensions = (
  element?: HTMLElement | null
): ElementDimensionType => {

  if (!element) element = window.document.body

  let [dimensions, setDimensions] = useState(() => getSize(element));

  let handleElementSizeChange = useCallback((element: HTMLElement) => {
    setDimensions(getSize(element));
  }, []);

  useLayoutEffect(() => {
    if (!element) return;
    if (typeof ResizeObserver !== 'function') return;

    handleElementSizeChange(element);

    let resizeObserver = new ResizeObserver(() => {
      handleElementSizeChange(element);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [element, handleElementSizeChange]);

  return dimensions;
};

export default useElementDimensions;
